import { useEffect, useState } from 'react';
import { initialsFromName } from '@/lib/utils/user.utils';

export interface ProfileLogoProps {
  /** Resolved image URL; initials show when it is missing or fails to load. */
  url?: string | null;
  /** Organisation or person name, for the initials fallback. */
  name: string;
  /** Edge length in pixels. */
  size?: number;
  /** `square` = company/firm logo tile (24px radius); `circle` = partner logo or a person. */
  shape?: 'square' | 'circle';
  /**
   * Outline: `dark` = the faint ring on the navy header, `light` = the white
   * ring where a logo overlaps a cover photo, `navy` = the ring that separates
   * a contact photo from the firm logo behind it.
   */
  ring?: 'none' | 'dark' | 'light' | 'navy';
  /** Initials tile colour. */
  tone?: 'teal2' | 'teal' | 'maroon';
}

const SHAPE_CLASS = { square: 'rounded-3xl', circle: 'rounded-full' } as const;
const RING_CLASS = {
  none: '',
  dark: 'border-4 border-white/12',
  light: 'border-[5px] border-white',
  navy: 'border-[3px] border-navy',
} as const;
const TONE_CLASS = { teal2: 'bg-teal2', teal: 'bg-teal', maroon: 'bg-maroon' } as const;

/** Logo, photo or initials tile for profile headers and cards (FR-164). */
export function ProfileLogo({
  url,
  name,
  size = 104,
  shape = 'square',
  ring = 'none',
  tone = 'teal2',
}: ProfileLogoProps) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  const style = { width: size, height: size, fontSize: Math.round(size / 3) };
  const frame = `${SHAPE_CLASS[shape]} ${RING_CLASS[ring]}`;

  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        style={style}
        className={`${frame} bg-white object-cover`}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={style}
      className={`${frame} ${TONE_CLASS[tone]} flex shrink-0 items-center justify-center font-semibold text-white`}
    >
      {initialsFromName(name)}
    </span>
  );
}
